import { Navigate, useNavigate } from 'react-router-dom';
import { useFlow } from '../state/flowStore';
import { CTAButton } from '../components/CTAButton';
import { FactorRow } from '../components/FactorRow';
import { MetricBar } from '../components/MetricBar';

export function BreakdownScreen() {
  const nav = useNavigate();
  const { scanResult, realAge } = useFlow();
  if (!scanResult) return <Navigate to="/" replace />;

  function rank(s: 'high' | 'moderate' | 'healthy') {
    return s === 'high' ? 2 : s === 'moderate' ? 1 : 0;
  }
  const concerns = scanResult.factors
    .filter(f => f.severity !== 'healthy')
    .sort((a, b) => rank(b.severity) - rank(a.severity))
    .slice(0, 2);
  const healthy = scanResult.factors.filter(f => f.severity === 'healthy');
  const wins =
    healthy.length >= 2
      ? healthy.slice(0, 2)
      : [...scanResult.factors]
          .sort((a, b) => rank(a.severity) - rank(b.severity))
          .slice(0, 2);

  const actionable = concerns.length + scanResult.factors.filter(f => f.severity === 'moderate').length;
  const potentialAge = Math.max(realAge - 3, scanResult.skinAge - 8);

  const metrics: [string, number][] = [
    ['Wrinkles', scanResult.scores.wrinkles.overall],
    ['Pigmentation', scanResult.scores.pigmentation.overall],
    ['Redness', scanResult.scores.redness.overall],
    ['Texture', scanResult.scores.texture.overall],
    ['Pores', scanResult.scores.pores.overall],
    ['Dark Circles', scanResult.scores.dark_circles.overall],
  ];
  if (scanResult.scores.hydration) metrics.push(['Hydration', scanResult.scores.hydration.overall]);
  if (scanResult.scores.acne) metrics.push(['Acne', scanResult.scores.acne.overall]);
  if (scanResult.scores.oiliness) metrics.push(['Oiliness', scanResult.scores.oiliness.overall]);
  if (scanResult.scores.firmness) metrics.push(['Firmness', scanResult.scores.firmness.overall]);

  return (
    <div className="lume-bg min-h-svh">
      <div
        className="px-6 pb-12 max-w-xl mx-auto"
        style={{ paddingTop: 'max(3.5rem, env(safe-area-inset-top))' }}
      >
        <div>
          <h1 className="text-headline text-text">What's driving this</h1>
          <p className="text-body text-text-secondary mt-2">
            We found {actionable} factors accelerating your skin age.
          </p>
        </div>

        {scanResult.summary && (
          <div className="mt-6 lume-card-bordered">
            <p className="lume-eyebrow">AI ANALYSIS</p>
            <p className="text-body text-text-secondary mt-2.5 leading-relaxed">
              {scanResult.summary}
            </p>
          </div>
        )}

        {concerns.length > 0 && (
          <section className="mt-7">
            <p className="lume-eyebrow">TOP CONCERNS</p>
            <p className="text-caption text-text-tertiary mt-1">
              Tap to see what this means for you.
            </p>
            <div className="mt-3 bg-surface rounded-card divide-y divide-surface-raised">
              {concerns.map(f => (
                <FactorRow key={f.name} factor={f} />
              ))}
            </div>
          </section>
        )}

        {wins.length > 0 && (
          <section className="mt-7">
            <p className="lume-eyebrow">WHERE YOU'RE WINNING</p>
            <div className="mt-3 bg-surface rounded-card divide-y divide-surface-raised">
              {wins.map(f => (
                <FactorRow key={`win-${f.name}`} factor={f} />
              ))}
            </div>
          </section>
        )}

        <section className="mt-7">
          <p className="lume-eyebrow">ALL METRICS</p>
          <div className="mt-3 lume-card flex flex-col gap-4">
            {metrics.map(([label, score]) => (
              <MetricBar key={label} label={label} score={score} />
            ))}
          </div>
        </section>

        <div className="mt-7 lume-card-bordered">
          <p className="lume-eyebrow">THE OPPORTUNITY</p>
          <p className="text-body text-text-secondary mt-2.5 leading-relaxed">
            Correct your top concerns and your skin could look {potentialAge} —{' '}
            {Math.max(0, scanResult.skinAge - potentialAge)} years younger than today.
          </p>
        </div>

        <div className="mt-8">
          <CTAButton label="See My Reversal Plan" onClick={() => nav('/paywall')} />
        </div>
      </div>
    </div>
  );
}
